import type { ProcessorLastAutomationDto, ProcessorPoolMemberDto } from '@/types/dtos/processor/processor-pool.dto';

export type ProcessorTranslate = (key: string, params?: Record<string, string | number>, fallback?: string) => string;

export type ProcessorAutomationSeverity = 'info' | 'success' | 'warning' | 'error';

export type ProcessorAutomationCaption = {
  text: string;
  severity: ProcessorAutomationSeverity;
  inFlight: boolean;
};

export function hasProcessorAuthIssue(processor: Pick<ProcessorPoolMemberDto, 'authHealth'>): boolean {
  return processor.authHealth === 'jwt_stale';
}

export function isProcessorAutomationInFlight(processor: Pick<ProcessorPoolMemberDto, 'lastAutomation'>): boolean {
  return processor.lastAutomation?.result === 'in_flight';
}

export function isProcessorHealStuck(
  processor: Pick<ProcessorPoolMemberDto, 'authHealth' | 'lastAutomation'>,
): boolean {
  if (isProcessorAutomationInFlight(processor)) {
    return false;
  }
  return hasProcessorAuthIssue(processor) || processor.lastAutomation?.result === 'failed';
}

export function resolveProcessorAutomationCaption(
  automation: ProcessorLastAutomationDto | undefined,
  translate: ProcessorTranslate,
): ProcessorAutomationCaption | null {
  if (!automation) {
    return null;
  }

  const kindLabel =
    automation.kind === 'jwt_reissue'
      ? translate('admin.processorAdmin.automation.kind.jwtReissue', undefined, 'JWT reissue')
      : automation.kind === 'soft_recover'
        ? translate('admin.processorAdmin.automation.kind.softRecover', undefined, 'Soft recovery')
        : translate('admin.processorAdmin.automation.kind.hardRecover', undefined, 'Hard recovery');

  if (automation.result === 'in_flight') {
    return { text: `${kindLabel} in flight`, severity: 'info', inFlight: true };
  }
  if (automation.result === 'failed') {
    return {
      text: `${kindLabel} failed — needs ops`,
      severity: 'error',
      inFlight: false,
    };
  }
  if (automation.result === 'skipped') {
    return {
      text: `${kindLabel} skipped`,
      severity: 'warning',
      inFlight: false,
    };
  }
  return { text: `${kindLabel} succeeded`, severity: 'success', inFlight: false };
}
